import { msg } from "../locales/translate.ts";
import { GD } from "../GD";
import type { IClient } from "./ClientService";
import { loadAllClients } from "./clientListLoader";
import type { ClientListRequest } from "./clientListRequest";

// In-memory reference data only. The main client page uses loadClientListPage.
export const CLIENT_DIRECTORY_TTL = 30 * 60 * 1000;
const entries = new Map<string, { controller: AbortController; promise: Promise<IClient[]>; expires: number }>();
let initialized = false;
let revision = 0;

export const invalidateClientDirectory = () => {
	revision++;
	for (const entry of entries.values()) entry.controller.abort();
	entries.clear();
};

const initialize = () => {
	if (initialized) return;
	initialized = true;
	GD.S_AUTH_LOG_OUT.subscribe(invalidateClientDirectory);
	GD.S_CLIENTS_UPDATE.subscribe(invalidateClientDirectory);
	GD.S_PROFILE_PATCH.subscribe(invalidateClientDirectory);
};

// Cancelling one picker must not cancel a shared request used by other consumers.
const waitFor = <T>(promise: Promise<T>, signal?: AbortSignal): Promise<T> => {
	if (!signal) return promise;
	signal.throwIfAborted();
	return new Promise((resolve, reject) => {
		const abort = () => reject(signal.reason);
		signal.addEventListener("abort", abort, { once: true });
		promise.then(value => { signal.removeEventListener("abort", abort); resolve(value); }, error => { signal.removeEventListener("abort", abort); reject(error); });
	});
};

export const loadClientDirectory = async (
	filters: ClientListRequest = {},
	context: Pick<IAPIRequest, "expectedProjectId" | "signal"> = {},
): Promise<IClient[]> => {
	initialize();
	context.signal?.throwIfAborted();
	const version = revision;
	const [project, profile] = await Promise.all([GD.REQ_CURRENT_PROJECT.request(), GD.REQ_PROFILE.request()]);
	if (version !== revision) throw new DOMException("Client directory invalidated", "AbortError");
	context.signal?.throwIfAborted();
	if (!project?.id || !profile || (context.expectedProjectId !== undefined && context.expectedProjectId !== project.id)) throw new Error(msg("selectAProjectToLoadClients"));
	// Preserve relationship/status/search filters for existing consumers; never request finance.
	const request: ClientListRequest = { with_finance: 0, per_page: 100, sort: "name", sort_dir: "asc" };
	for (const key of ["acquirerId", "terminalId", "channelId", "status", "search"] as const) {
		if (filters[key] !== undefined && filters[key] !== "") Object.assign(request, { [key]: filters[key] });
	}
	const roles = profile.projects.find(item => item.id === project.id)?.roles ?? project.roles;
	const key = JSON.stringify([profile.id, project.id, roles, request]);
	let entry = entries.get(key);
	if (entry && entry.expires <= Date.now()) { entries.delete(key); entry = undefined; }
	if (!entry) {
		// Keep the directory bounded when many relationship filters are used.
		if (entries.size >= 20) {
			const oldest = entries.keys().next().value!;
			entries.get(oldest)?.controller.abort(); entries.delete(oldest);
		}
		const controller = new AbortController();
		const next = { controller, expires: Infinity, promise: Promise.resolve([] as IClient[]) };
		next.promise = loadAllClients(request, { expectedProjectId: project.id, signal: controller.signal }).then(result => {
			controller.signal.throwIfAborted();
			next.expires = Date.now() + CLIENT_DIRECTORY_TTL;
			return result.items.map(({ finance: _finance, ...client }) => client);
		}).catch(error => { if (entries.get(key) === next) entries.delete(key); throw error; });
		entries.set(key, next);
		entry = next;
	}
	// Callers may sort/edit their copy without corrupting shared reference data.
	return structuredClone(await waitFor(entry.promise, context.signal));
};
